import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useNotifications } from '../context/NotificationContext';
import { createLogger } from '../utils/logger';

const logger = createLogger({ module: 'useBannerAutoRead' });

/**
 * Hook to automatically mark banner notifications as read when landing on a page.
 * 
 * Rules:
 * - If there's exactly 1 unread banner notification for the current page, auto-mark it as read
 * - If there are multiple unread banners, user must interact with them (carousel or notification panel)
 * - Only runs once per page visit to avoid excessive API calls
 */
export const useBannerAutoRead = (delay: number = 1000) => {
  const location = useLocation();
  const { autoMarkBannersAsReadForPage } = useNotifications();

  useEffect(() => {
    const currentPage = location.pathname;
    
    logger.debug('Page visited, checking for banner auto-read', { 
      page: currentPage,
      delay 
    });

    // Add a small delay to ensure banner notifications have loaded
    const timeoutId = setTimeout(async () => {
      try {
        await autoMarkBannersAsReadForPage(currentPage);
      } catch (error) {
        logger.error('Error in banner auto-read', { 
          page: currentPage, 
          error 
        });
      }
    }, delay);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [location.pathname, autoMarkBannersAsReadForPage, delay]);
};

/**
 * Hook variant that allows manual triggering of banner auto-read
 * Useful for components that need to control when the auto-read happens
 */
export const useBannerAutoReadManual = () => {
  const location = useLocation();
  const { autoMarkBannersAsReadForPage } = useNotifications();

  const triggerAutoRead = async (customPage?: string) => {
    const page = customPage || location.pathname;
    
    logger.debug('Manual banner auto-read triggered', { page });
    
    try {
      await autoMarkBannersAsReadForPage(page);
    } catch (error) {
      logger.error('Error in manual banner auto-read', { page, error });
    }
  };

  return { triggerAutoRead };
};